// net-base.js — the ONE place that turns a relative app path into the URL the
// running build should actually hit. It exists so the SAME source can serve two
// deploy shapes without a runtime toggle:
//
//   • WEB build (the shipped SPA): the Express server serves the SPA and the
//     /api, /uploads, /assets paths on the SAME origin, so a bare relative path
//     ("/api/v1/opd/queue-view") is correct as-is. net-base returns it UNCHANGED
//     — byte-identical to pre-net-base behaviour. No serverBase in the config →
//     this is always the case on the web.
//
//   • CAPACITOR build (main-2 tablet app): the WebView origin is https://localhost
//     (or an app:// scheme), NOT the server. A relative "/api/…" would resolve
//     against the WebView origin and 404. The loaded Medplum config then carries
//     a `serverBase` (e.g. "http://10.0.2.2:8103" in dev / the https server URL
//     in prod) and net-base prefixes it so the call reaches the real server.
//
// serverBase is plumbed in ONCE by medplum-client.js appBundle() (setServerBase,
// from the same loadConfig() the whole app already awaits) — net-base does not
// fetch config itself, so there is no import cycle (medplum-client → net-base,
// never the reverse). Until that init runs, serverBase is '' and every helper is
// the identity/same-origin function, so a web build is unaffected even if a call
// site fires before config resolves.
//
// Direct Medplum reads/writes do NOT go through here — MedplumClient already gets
// its absolute baseUrl from the config. net-base is only for the app's OWN server
// legs (Express /api, /uploads, /assets) and the kiosk-voice WebSocket.

let serverBase = ''; // '' = same-origin (web). Set to an absolute origin for Capacitor.

/**
 * Called once by medplum-client.js after the app config loads. `base` is the
 * optional `serverBase` field (absent on web → stays ''). A trailing slash is
 * trimmed so `abs('/api')` never produces a double slash.
 */
export function setServerBase(base) {
  serverBase = base ? String(base).replace(/\/+$/, '') : '';
}

/** The configured server origin, or '' when same-origin (web). Mostly for tests/debug. */
export function getServerBase() {
  return serverBase;
}

// Prefix an app-relative path with serverBase, or return it unchanged when
// same-origin. A path that is already absolute (http(s)://, blob:, data:, //) is
// returned untouched — callers sometimes pass a full URL.
function abs(path) {
  const p = String(path == null ? '' : path);
  if (!serverBase) return p;
  if (/^[a-z]+:\/\//i.test(p) || p.startsWith('//') || p.startsWith('blob:') || p.startsWith('data:')) return p;
  return serverBase + (p.startsWith('/') ? p : '/' + p);
}

/** URL for an Express API call (/api/v1/…). Unchanged on web; absolute on Capacitor. */
export function apiUrl(path) {
  return abs(path);
}

/** URL for a clinical upload byte fetch (/uploads/…). Unchanged on web; absolute on Capacitor. */
export function uploadsUrl(path) {
  return abs(path);
}

/** URL for a static asset (/assets/…). Unchanged on web; absolute on Capacitor. */
export function assetUrl(path) {
  return abs(path);
}

/**
 * WebSocket URL for a server WS leg (e.g. the kiosk-voice STT socket). A WS needs
 * an absolute ws(s):// URL — a bare relative path is not portable — so even the
 * same-origin (web) case is derived from window.location, reproducing the exact
 * URL the call sites built inline before net-base. With serverBase set, the
 * server origin's scheme is mapped http→ws / https→wss.
 *
 * `path` may already include a query string (e.g. "/…/stt-ws?token=abc").
 */
export function wsUrl(path) {
  const p = String(path == null ? '' : path);
  if (!serverBase) {
    const proto = (typeof location !== 'undefined' && location.protocol === 'https:') ? 'wss:' : 'ws:';
    const host = (typeof location !== 'undefined' && location.host) || '';
    return `${proto}//${host}${p.startsWith('/') ? p : '/' + p}`;
  }
  const wsOrigin = serverBase.replace(/^http:/i, 'ws:').replace(/^https:/i, 'wss:');
  return wsOrigin + (p.startsWith('/') ? p : '/' + p);
}
